// API
import {
  buildSimilarFloorplansFilter,
  getListingByAddress,
  growthClientWithRequestContext,
} from '@zg-rentals/rental-growth-api';
import { RentalGrowthUserPropertyListingWebApiHandler_getByAddress } from '@zg-rentals/ts-rental-growth-api-client';

// Actions
import { setActiveId } from './UIActions';

// Models
import type AutocompleteAddress from '../../models/AutocompleteAddress';
import Address from '../../models/Address';
import Comparables from '../../models/Comparables';

// Misc / utils
import type { ApiQueryParamsType } from '../../types/ApiQueryParamsType';
import { augmentError } from '../../utils/errors';
import constants from '../../flux/constants';
import errorConstants from '../../constants/ErrorConstants';
import { emitErrorEvent } from 'app/shared/constants/PriceMyRentalCSEvents';

// Types
import type { ByAddressApiType, ComparablesApiType } from '../../types/ApiResponseTypes';

type AugmentedAutocompleteAddressType = AutocompleteAddress & {
  isRecent: boolean;
  unit: string;
};

export const getComparables = (params: ApiQueryParamsType) => {
  // @ts-ignore
  return async (dispatch) => {
    const { data } = await dispatch(fetchListingByAddress(params));
    const userComps = dispatch(setComparables(data));

    return { userComps };
  };
};

export const getAddressAndComparables = (params: AugmentedAutocompleteAddressType) => {
  // @ts-ignore
  return async (dispatch) => {
    const { data } = await dispatch(fetchListingByAddress(params));
    const userAddress = await dispatch(setAddress(data));
    const hasZestimate = Boolean(data.byAddress?.floorplans?.[0]?.zestimate?.rentZestimate);
    const hasCompsPrices = Boolean(data.byAddress?.similarFloorplans?.floorplans?.length);

    if (!hasCompsPrices && !hasZestimate) {
      throwNonCriticalError({
        message: "We don't have enough data for this address",
        code: errorConstants.RENT_ZESTIMATE_NOT_FOUND,
      });
    }

    if (hasCompsPrices) {
      await dispatch(setComparables(data));
    }

    return {
      userAddress,
    };
  };
};

export const fetchListingByAddress = (params: ApiQueryParamsType) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    const { street, city, state, zip, unit } = params;
    try {
      const user = getState().user;
      const response = await getListingByAddress(
        {
          street: street ?? '',
          city: city ?? '',
          state: state ?? '',
          zip: zip ?? '',
          ...(unit != null && { unit }),
          similarFloorplansFilter: buildSimilarFloorplansFilter(params),
        },
        growthClientWithRequestContext(getState()),
      );

      const data = {
        byAddress: {
          geo: response.geo,
          floorplans: response.floorplans,
          marketSummary: response.marketSummary,
          similarFloorplans: response.similarFloorplans,
          stats: response.stats,
        },
      };

      if (!response.floorplans?.length) {
        emitErrorEvent(user);
        throwNonCriticalError({
          message: 'We were unable to match this address',
          code: errorConstants.PROPERTY_DETAILS_NOT_FOUND,
        });
      }
      if (response.similarFloorplansError || !response.similarFloorplans?.floorplans?.length) {
        emitErrorEvent(user);
      }
      if (!response.floorplans?.[0]?.zestimate?.rentZestimate) {
        emitErrorEvent(user);
      }

      return { data };
    } catch (error) {
      augmentError(error, {
        action: 'Error in AddressActions#fetchListingByAddress',
      });

      throw error;
    }
  };
};

// Setters
export const setAddress = (data: ByAddressApiType) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    const addressData = {
      ...data.byAddress.floorplans[0],
      geo: data.byAddress.geo,
      marketSummary: data.byAddress?.marketSummary,
    };

    // Check if the user logged in - if so, check if they already have a listing for this address
    const isUserLoggedIn: boolean = getState().user.loggedIn;
    if (isUserLoggedIn) {
      const { street, city, state, zip, unit } = addressData.address;
      const fullAddress = street + ',' + city + ', ' + state + ' ' + zip;

      await RentalGrowthUserPropertyListingWebApiHandler_getByAddress(
        { fullAddress, unit },
        growthClientWithRequestContext(getState()),
      )
        .then((res) => {
          addressData.addressHasListing = res.listingFound;
          // If listingFound false, then there is no listing (a.k.a undefined)
          if (res.listingFound) {
            addressData.zrmListingId = res.listingAlias;
          }
          return;
        })
        .catch((error: { message: string }) => {
          augmentError(error, {
            action: 'Error in AddressActions#setAddress',
          });

          throw error;
        });
    }

    // Set the user's address
    const modeledAddressData = new Address(addressData);

    dispatch({
      type: constants.SET_ADDRESS,
      payload: modeledAddressData,
    });

    if (modeledAddressData.listingId) {
      dispatch(setActiveId(modeledAddressData.listingId));
    }

    return modeledAddressData;
  };
};

export const setComparables = (data: ByAddressApiType) => {
  // @ts-ignore
  return (dispatch, getState) => {
    const user = getState().user;

    // The REST getByAddress endpoint omits similarFloorplans on error / no comparables, whereas the
    // GraphQL path always returned an object. Without this guard, destructuring it throws.
    const similarFloorplans = data.byAddress?.similarFloorplans;
    if (!similarFloorplans) {
      emitErrorEvent(user);
      return [];
    }

    const { floorplans, listingRanking, lowestPriceIncluded: min, highestPriceIncluded: max } = similarFloorplans;

    if (floorplans.length === 0) {
      emitErrorEvent(user);
    }

    // Set the user's comparables
    const compsItems = floorplans.map((comp: ComparablesApiType) => {
      return new Comparables(comp);
    });

    const userProperty = getState().address;
    compsItems.splice(listingRanking, 0, userProperty);
    const userPropertyIdx = compsItems.findIndex((comp) => comp.isUsersComp);

    dispatch({
      type: constants.SET_COMPARABLES,
      payload: {
        min,
        max,
        items: compsItems,
        userPropertyIdx,
      },
    });

    // set activeId to userProperty ID after comps load to scroll to home
    dispatch(setActiveId(userProperty.listingId));

    return compsItems;
  };
};

// Errors
const throwNonCriticalError = ({ message, code }: { message: string; code: string }) => {
  throw {
    nonCriticalError: true,
    errorCode: code,
    errorMsg: message,
  };
};
